import { useEffect, useState } from "react";
import {
  createTheme,
  ThemeProvider,
  responsiveFontSizes,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import Layout from "components/layout/Layout";
import CaladreTheme from "theme/Theme";
import { privateRoutes, publicRoutes } from "routes/Routes";
import {
  IS_AUTHENTICATED,
  LOAD_DATA,
  SET_TOKEN,
  SET_USER,
} from "slices/global.slice";
import axios from "axios";
import { clearParamsFromCurrentUrl } from "utils/url";
import NotificationService, { ChatMessage } from "services/notificationService";
import userService from "services/userService";
import ScrollToTop from "components/scroller";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent"
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { v4 as uuidv4 } from 'uuid';
import { AdsProvider } from "hooks/useAds";

var sessionId = uuidv4();
var pageTime = Date.now();
var sessionTime = Date.now();

const App = () => {
  let theme = createTheme(CaladreTheme);
  theme = responsiveFontSizes(theme);
  const { dataLoaded, isAuthenticated, token } = useSelector(
    (state: RootState) => state.globalSlice,
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [previousLocation, setPreviousLocation] = useState(location.pathname);

  const declineCookies = () => {
    localStorage.removeItem('token');
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paramToken = urlParams.get("token");
    const storedToken = getCookieConsentValue() === 'true' ? localStorage.getItem("token") : undefined;
    if (paramToken || storedToken) {
      NotificationService().connect(paramToken || storedToken);
      dispatch(SET_TOKEN(paramToken || storedToken));
    }
    clearParamsFromCurrentUrl();
  }, [location]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      localStorage.setItem("token", token);
      dispatch(IS_AUTHENTICATED(true));
      userService.getCurrentUser().then((user) => {
        dispatch(SET_USER(user));
      });
    }
  }, [token, location]);

  useEffect(() => {
    logDuration();
  }, [location]);

  useEffect(() => {
    sendSessionDuration();
    const interval = setInterval(() => sendSessionDuration(), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    dispatch(LOAD_DATA());
    const interval = setInterval(() => dispatch(LOAD_DATA()), 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const sendSessionDuration = () => {
    const duration = Date.now() - sessionTime;

    const template = '{sessionId} - {duration}';
    const log = `${sessionId};${duration}`;
    userService.addLog(log, template);
  }

  const logDuration = () => {
    const duration = Date.now() - pageTime;
    pageTime = Date.now();

    const template = '{sessionId} - {page} - {duration}';
    const log = `${sessionId};${previousLocation};${duration}`;
    userService.addLog(log, template);
    setPreviousLocation(location.pathname);
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <AdsProvider>
          <CssBaseline />
          <ScrollToTop />
          <Layout>
            <CssBaseline />
            <Routes>
              {publicRoutes.map(({ key, path, Component }) => (
                <Route key={key} path={path} element={<Component />} />
              ))}

              {privateRoutes.map(({ key, path, Component }) => (
                <Route
                  key={key}
                  path={path}
                  element={
                    isAuthenticated ? (
                      <Component />
                    ) : (
                      isAuthenticated != null && <Navigate to="/auth" replace />
                    )
                  }
                />
              ))}
            </Routes>
          </Layout>


          {!isAuthenticated && !(location.pathname === "/auth") && false && (
            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                color: "#fff",
                textAlign: "center",
                py: 2,
                px: 3,
                zIndex: 1300,
                touchAction: "none"
              }}
            > <Typography
              variant={isMobile ? "h6" : "h3"}
              gutterBottom
              color="white"
            >
                Inscris toi et découvre gratuitement ta destination
              </Typography>
              <Typography
                variant={isMobile ? "body2" : "h4"}
                gutterBottom
                color="white"
              >
                Viens découvrir qui voyage vers ta destination, Fais parti.e de la
                communauté Caladre
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{ touchAction: "auto" }}
                onClick={() => navigate("/auth")}
              >
                Je m'inscris
              </Button>
            </Box>
          )}
        </AdsProvider>
      </ThemeProvider>
      {
        (isAuthenticated || location.pathname === "/auth") && <CookieConsent
          overlay={true}
          location='bottom'
          enableDeclineButton
          declineButtonText='Refuser'
          buttonText='Accepter'
          onDecline={declineCookies}
          containerClasses="cookies"
        >
          <Box style={{ height: '100px' }}>
            <Typography>Ce site utilise des cookies uniquement pour vous reconnecter.</Typography>
          </Box>
        </CookieConsent>
      }
    </>
  );
};

export default App;